import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/brand/Logo';
import { Spinner } from '../components/ui/spinner';
import { useAuthStore } from '../store/auth';

/**
 * Redeems the emailed sandbox link (`/try/verify?token=…`, Feature 24): verifies
 * the single-use token, provisions the sandbox, and drops the visitor into the
 * app. An invalid/expired token shows a friendly "request a new one" prompt.
 */
export function TrySandboxVerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const completeSandbox = useAuthStore((s) => s.completeSandbox);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get('token');
    if (!token) {
      setError('This sandbox link is missing its token.');
      return;
    }
    void (async () => {
      try {
        await completeSandbox(token);
        navigate('/', { replace: true });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'This sandbox link is invalid or has expired.',
        );
      }
    })();
  }, [params, completeSandbox, navigate]);

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center gap-6 px-6 py-16">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background: 'radial-gradient(55% 45% at 50% 0%, var(--iris-dim), transparent 70%)',
        }}
      />
      <Logo size={34} />
      {error ? (
        <div className="w-full max-w-sm text-center">
          <h1 className="font-display text-2xl font-bold text-bone">Link didn’t work</h1>
          <p className="mt-2 text-sm text-muted">{error}</p>
          <Link to="/try" className="mt-4 inline-block text-sm text-iris hover:text-iris-strong">
            Request a new sandbox link →
          </Link>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Spinner />
          <p className="text-sm text-muted">Opening your sandbox…</p>
        </div>
      )}
    </div>
  );
}
